import React, { useState, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserAvatarColor, getUserInitials, getUserDisplayName } from '../utils/avatarUtils';
import { Trash2Icon, AlertCircleIcon } from './icons/LucideIcons';
import { useAppearance } from '../contexts/AppearanceContext';

// Forest tints the tab icon separately from its label; Fable renders the bare icon.
const TabIcon = ({ forest, active, children }) => (forest
  ? <span className={`flex ${active ? 'text-floral-700' : 'text-gray-500'}`}>{children}</span>
  : <>{children}</>);

const MobileBottomNav = () => {
  const [showMore, setShowMore] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef({ active: false, startY: 0, startTime: 0 });
  const navigate = useNavigate();
  const { isAdmin, user, logout } = useAuth();
  // Forest: active tab = floral-900 label with a floral-700 icon, rest in the
  // gray ink steps. Fable keeps its single floral-700 / gray-400 color.
  const { appearance } = useAppearance();
  const isForest = appearance !== 'fable';
  const tabTone = (active) => (isForest
    ? (active ? 'text-floral-900' : 'text-gray-600')
    : (active ? 'text-floral-700' : 'text-gray-400'));

  const handleMoreNav = (path) => {
    navigate(path);
    setShowMore(false);
    setShowAccount(false);
  };

  const handleLogout = async () => {
    setShowMore(false);
    setShowAccount(false);
    await logout();
    navigate('/');
  };

  // Drag handle: drag-down-to-close (distance or velocity) plus tap-to-close.
  // State stored in a ref so mid-drag reads are never stale.
  const handleDragStart = (e) => {
    const touch = e.touches ? e.touches[0] : e;
    dragStateRef.current = { active: true, startY: touch.clientY, startTime: Date.now() };
    setIsDragging(true);
  };

  const handleDragMove = (e) => {
    if (!dragStateRef.current.active) return;
    const touch = e.touches ? e.touches[0] : e;
    const delta = touch.clientY - dragStateRef.current.startY;
    // Only allow downward drag — we're closing, not opening
    setDragY(Math.max(0, delta));
  };

  const handleDragEnd = () => {
    if (!dragStateRef.current.active) return;
    const totalDelta = dragY;
    const elapsed = Math.max(Date.now() - dragStateRef.current.startTime, 1);
    const velocity = totalDelta / elapsed; // px/ms

    const DISTANCE_THRESHOLD = 80;
    const VELOCITY_THRESHOLD = 0.5; // ≈ a quick flick
    const TAP_THRESHOLD = 5;

    // Close if: dragged far enough, flicked fast enough, or barely moved (= tap)
    const shouldClose =
      totalDelta >= DISTANCE_THRESHOLD ||
      velocity >= VELOCITY_THRESHOLD ||
      totalDelta < TAP_THRESHOLD;

    dragStateRef.current.active = false;
    setIsDragging(false);
    setDragY(0);
    if (shouldClose) setShowMore(false);
  };

  const handleDragKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setShowMore(false);
    }
  };

  const moreItems = [
    { path: '/dispatch', label: 'Dispatch', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" />
      </svg>
    )},
    { path: '/scan', label: 'Scan', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0 1 3.75 9.375v-4.5ZM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5ZM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 0 1-1.125-1.125v-4.5Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75ZM6.75 16.5h.75v.75h-.75v-.75ZM16.5 6.75h.75v.75h-.75v-.75ZM13.5 13.5h.75v.75h-.75v-.75ZM13.5 19.5h.75v.75h-.75v-.75ZM19.5 13.5h.75v.75h-.75v-.75ZM19.5 19.5h.75v.75h-.75v-.75ZM16.5 16.5h.75v.75h-.75v-.75Z" />
      </svg>
    )},
    { path: '/customers', label: 'Customers', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
      </svg>
    )},
    { path: '/events', label: 'Events', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
      </svg>
    )},
    { path: '/issues', label: 'Issues', icon: <AlertCircleIcon size={20} /> },
    { path: '/waste', label: 'Waste', icon: <Trash2Icon size={20} /> },
    { path: '/reports', label: 'Reports', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
      </svg>
    )},
    { path: '/recurring-review', label: 'Recurring', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182" />
      </svg>
    )},
    { path: '/settings', label: 'Settings', icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>
    )},
  ];

  if (isAdmin()) {
    moreItems.push({
      path: '/user-management',
      label: 'Users',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
        </svg>
      ),
    });
  }

  const initials = user ? getUserInitials(user) : '?';
  const displayName = user ? getUserDisplayName(user) : 'Guest';
  const avatarColor = user ? getUserAvatarColor(user) : 'bg-gray-400';

  const navLinkClass = ({ isActive }) =>
    `flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-medium transition-colors ${
      tabTone(isActive)
    }`;

  return (
    <>
      {/* More sheet backdrop */}
      {showMore && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => { setShowMore(false); setShowAccount(false); }}
        />
      )}

      {/* More sheet */}
      {showMore && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white rounded-t-2xl shadow-2xl border-t border-gray-200"
          style={{
            transform: `translateY(${dragY}px)`,
            transition: isDragging ? 'none' : 'transform 200ms ease-out',
          }}
        >
          <div
            className="flex justify-center items-center py-3 cursor-grab active:cursor-grabbing touch-none select-none"
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
            onTouchCancel={handleDragEnd}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onKeyDown={handleDragKey}
            role="button"
            tabIndex={0}
            aria-label="Drag down or tap to close menu"
          >
            <div className="w-10 h-1.5 bg-gray-300 rounded-full" />
          </div>

          {/* Nav grid */}
          <div className="px-4 grid grid-cols-3 gap-2">
            {moreItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleMoreNav(item.path)}
                className="flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                <span className="text-gray-600">{item.icon}</span>
                <span className="text-xs text-gray-700 font-medium">{item.label}</span>
              </button>
            ))}
          </div>

          {/* Account accordion */}
          <div className="mx-4 border-t border-gray-200" />
          <div className="px-4 pt-3 pb-20">
            <button
              onClick={() => setShowAccount(!showAccount)}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <div className={`w-8 h-8 ${avatarColor} rounded-full flex items-center justify-center text-white text-sm font-medium shrink-0`}>
                {initials}
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{displayName}</div>
                {user?.email && <div className="text-xs text-gray-500 truncate">{user.email}</div>}
              </div>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"
                className={`w-4 h-4 text-gray-400 transition-transform ${showAccount ? 'rotate-180' : ''}`}>
                <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
              </svg>
            </button>

            {showAccount && (
              <div className="mt-1 ml-11 space-y-1">
                <button
                  onClick={() => handleMoreNav('/profile')}
                  className="w-full text-left px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50 active:bg-gray-100"
                >
                  Your Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 text-sm text-red-600 rounded-lg hover:bg-red-50 active:bg-red-100 [html[data-scheme=dark]:not([data-appearance=fable])_&]:text-danger-fg [html[data-scheme=dark]:not([data-appearance=fable])_&]:hover:bg-danger-bg [html[data-scheme=dark]:not([data-appearance=fable])_&]:active:bg-danger-bg"
                >
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Bottom navigation bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-stretch">
          <NavLink to="/" end className={navLinkClass} onClick={() => { setShowMore(false); setShowAccount(false); }}>
            {({ isActive }) => (<>
              <TabIcon forest={isForest} active={isActive}>
              <svg xmlns="http://www.w3.org/2000/svg" fill={isActive ? 'currentColor' : 'none'} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25A2.25 2.25 0 0 1 13.5 18v-2.25Z" />
              </svg>
              </TabIcon>
              <span>Home</span>
            </>)}
          </NavLink>

          <NavLink to="/orders" className={navLinkClass} onClick={() => { setShowMore(false); setShowAccount(false); }}>
            {({ isActive }) => (<>
              <TabIcon forest={isForest} active={isActive}>
              <svg xmlns="http://www.w3.org/2000/svg" fill={isActive ? 'currentColor' : 'none'} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z" />
              </svg>
              </TabIcon>
              <span>Orders</span>
            </>)}
          </NavLink>

          <NavLink to="/create-order" className={navLinkClass} onClick={() => { setShowMore(false); setShowAccount(false); }}>
            {({ isActive }) => (<>
              <div className={`rounded-full p-1.5 ${isActive ? 'bg-floral-700' : 'bg-floral-600'}`}>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="white" className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
              </div>
              <span>New</span>
            </>)}
          </NavLink>

          <NavLink to="/formulas" className={navLinkClass} onClick={() => { setShowMore(false); setShowAccount(false); }}>
            {({ isActive }) => (<>
              <TabIcon forest={isForest} active={isActive}>
              <svg xmlns="http://www.w3.org/2000/svg" fill={isActive ? 'currentColor' : 'none'} viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 15.75V18m-7.5-6.75h.008v.008H8.25v-.008ZM8.25 15h.008v.008H8.25V15Zm0 2.25h.008v.008H8.25v-.008ZM10.5 11.25h.008v.008H10.5v-.008ZM10.5 15h.008v.008H10.5V15Zm0 2.25h.008v.008H10.5v-.008ZM12.75 11.25h.008v.008h-.008v-.008ZM12.75 15h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008ZM15 11.25h.008v.008H15v-.008ZM15 15h.008v.008H15V15Zm-3.75 6h7.5a2.25 2.25 0 0 0 2.25-2.25V5.25A2.25 2.25 0 0 0 18.75 3H5.25A2.25 2.25 0 0 0 3 5.25v13.5A2.25 2.25 0 0 0 5.25 21h6Z" />
              </svg>
              </TabIcon>
              <span>Formulas</span>
            </>)}
          </NavLink>

          <button
            onClick={() => setShowMore(!showMore)}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 text-[10px] font-medium transition-colors ${
              tabTone(showMore)
            }`}
          >
            <TabIcon forest={isForest} active={showMore}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
            </svg>
            </TabIcon>
            <span>More</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default MobileBottomNav;
